import {
  type JSX,
  type Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";

import { DateRecurrencePicker } from "./DateRecurrencePicker";
import {
  type DateRecurrencePickerDataProps,
  dateRecurrencePickerDataSchema,
} from "./DateRecurrencePicker.schema";
import type { RecurrenceException } from "./DateRecurrencePicker.types";
import { DATE_RECURRENCE_PICKER_TAG_NAME } from "./constants";

export interface DateRecurrencePickerHandle {
  value: (value: string) => void;
  exceptions: (exceptions: RecurrenceException[]) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [DATE_RECURRENCE_PICKER_TAG_NAME]: {
      props: DateRecurrencePickerDataProps;
      handle: DateRecurrencePickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-date-recurrence-picker": HTMLElement & DateRecurrencePickerHandle;
  }
}

export function RWCDateRecurrencePicker({
  ref,
  value: initialValue,
  exceptions: initialExceptions,
  exceptionsName,
  disabled: initialDisabled,
  showEnd,
  tour,
  locale,
  variant,
  size,
  placeholder,
}: DateRecurrencePickerDataProps & {
  ref?: Ref<DateRecurrencePickerHandle>;
}): JSX.Element {
  const host = useIslandHost();

  // A page rarely spells its locale out on the element; it is the document's.
  const documentLocale = useIntlLocale();

  const [value, setValue] = useState(initialValue ?? "");

  const [exceptions, setExceptions] = useState(initialExceptions);

  const [disabled, setDisabled] = useState(initialDisabled);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      exceptions: setExceptions,
      disabled: (next = true) => setDisabled(next),
    }),
    [],
  );

  useEffect(() => {
    const internals = getElementInternals(host);

    if (!internals) {
      return;
    }

    if (disabled) {
      internals.setFormValue(null);

      return;
    }

    // One element, two fields: the rule under the element's own name, and the
    // exceptions under theirs when the page asked for them.
    if (!exceptionsName) {
      internals.setFormValue(value);

      return;
    }

    const data = new FormData();

    data.append(host?.getAttribute("name") ?? "", value);
    data.append(exceptionsName, JSON.stringify(exceptions));

    internals.setFormValue(data);
  }, [host, value, exceptions, exceptionsName, disabled]);

  function emit(type: string, detail: Record<string, unknown>) {
    host?.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
  }

  return (
    <DateRecurrencePicker
      locale={locale ?? documentLocale}
      variant={variant}
      size={size}
      placeholder={placeholder}
      value={value}
      onValueChange={(next) => {
        setValue(next);
        emit("recurrence:change", { value: next });
      }}
      exceptions={exceptions}
      onExceptionsChange={
        exceptionsName
          ? (next) => {
              setExceptions(next);
              emit("recurrence:exceptions", { exceptions: next });
            }
          : undefined
      }
      showEnd={showEnd}
      tour={tour}
      disabled={disabled}
    />
  );
}

componentManager.register(
  rwc(DATE_RECURRENCE_PICKER_TAG_NAME, {
    component: RWCDateRecurrencePicker,
    dataSchema: dateRecurrencePickerDataSchema,
    formAssociated: true,
  }),
);
